import { Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'

import type { Vitals } from '@snootsvet/shared'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/frontend/components/ui/alert-dialog'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import { VitalsChips } from '@/frontend/features/patient/vitals-chip'
import { VitalsFormDialog } from '@/frontend/features/patient/vitals-form-dialog'
import { useDeleteVitals } from '@/frontend/hooks/api/use-delete-vitals'

/** Timeline entry for vitals not linked to a consultation. */
export function VitalsEntry({ vitals }: { vitals: Vitals }) {
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const { mutate: deleteVitals, isPending: isDeleting } = useDeleteVitals({
    patientId: vitals.patientId,
  })

  return (
    <>
      <div className="flex items-center gap-3 rounded-xl bg-card px-4 py-3 ring-1 ring-foreground/10">
        <div className="flex flex-1 flex-wrap gap-2">
          <VitalsChips vitals={vitals} recordedAt={vitals.createdAt} />
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {vitals.source === 'member' && (
            <Badge variant="outline" size="sm">
              Owner-submitted
            </Badge>
          )}
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => setEditOpen(true)}
            aria-label="Edit vitals"
          >
            <Pencil className="size-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => setDeleteOpen(true)}
            aria-label="Delete vitals"
            className="text-destructive-fg"
          >
            <Trash2 className="size-3" />
          </Button>
        </div>
      </div>

      <VitalsFormDialog
        patientId={vitals.patientId}
        vitals={vitals}
        open={editOpen}
        onOpenChange={setEditOpen}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete vitals?</AlertDialogTitle>
            <AlertDialogDescription>
              This vitals record will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={isDeleting}
              onClick={() => deleteVitals(vitals.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
